'use client'

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { X, Check, AlertCircle, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  timestamp: Date
  read: boolean
  action?: {
    label: string
    onClick: () => void
  }
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  removeNotification: (id: string) => void
  clearAll: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const unreadCount = notifications.filter(n => !n.read).length

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false,
    }
    setNotifications(prev => [newNotification, ...prev])

    // Show browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/icon-192x192.png',
      })
    }
  }, [])

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    )
  }, [])

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }, [])

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const clearAll = useCallback(() => {
    setNotifications([])
  }, [])

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // TODO: Replace with real WebSocket/SSE notifications in production
  // Demo notifications disabled - uncomment for testing
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const random = Math.random()
  //     if (random < 0.1) { // 10% chance every 30 seconds
  //       const demoNotifications = [
  //         {
  //           title: 'New Template Created',
  //           message: 'A new template "Sales Report" has been created',
  //           type: 'info' as const,
  //         },
  //         {
  //           title: 'Workflow Completed',
  //           message: 'Document generation workflow finished successfully',
  //           type: 'success' as const,
  //         },
  //         {
  //           title: 'Storage Warning',
  //           message: 'You are using 85% of your storage quota',
  //           type: 'warning' as const,
  //         },
  //       ]
  //       const notification = demoNotifications[Math.floor(Math.random() * demoNotifications.length)]
  //       if (notification) {
  //         addNotification(notification)
  //       }
  //     }
  //   }, 30000) // Check every 30 seconds
  //
  //   return () => clearInterval(interval)
  // }, [addNotification])

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

// Notification Toast Component
export function NotificationToast() {
  const { notifications, removeNotification } = useNotifications()
  const recentNotifications = notifications.slice(0, 3).filter(n => {
    const age = Date.now() - n.timestamp.getTime()
    return age < 5000 // Show for 5 seconds
  })

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <Check className="h-5 w-5" />
      case 'warning':
        return <AlertCircle className="h-5 w-5" />
      case 'error':
        return <AlertCircle className="h-5 w-5" />
      default:
        return <Info className="h-5 w-5" />
    }
  }

  const getStyles = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 text-green-900 border-green-200 dark:bg-green-900 dark:text-green-100 dark:border-green-800'
      case 'warning':
        return 'bg-yellow-50 text-yellow-900 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-100 dark:border-yellow-800'
      case 'error':
        return 'bg-red-50 text-red-900 border-red-200 dark:bg-red-900 dark:text-red-100 dark:border-red-800'
      default:
        return 'bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-800'
    }
  }

  if (recentNotifications.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {recentNotifications.map(notification => (
        <div
          key={notification.id}
          className={cn(
            'flex items-start gap-3 rounded-lg border p-4 shadow-lg transition-all',
            'animate-in slide-in-from-bottom-2 duration-300',
            getStyles(notification.type)
          )}
          style={{ minWidth: '320px', maxWidth: '420px' }}
        >
          <div className="flex-shrink-0">
            {getIcon(notification.type)}
          </div>
          <div className="flex-1">
            <h4 className="font-semibold">{notification.title}</h4>
            <p className="text-sm mt-1 opacity-90">{notification.message}</p>
            {notification.action && (
              <Button
                variant="link"
                size="sm"
                className="p-0 h-auto mt-2 font-medium"
                onClick={notification.action.onClick}
              >
                {notification.action.label}
              </Button>
            )}
          </div>
          <button
            onClick={() => removeNotification(notification.id)}
            className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}